// ============================================================================
// TrendCharts — the trend section of the metrics view. A switchable set of
// vivid SVG trends from the metrics `series`:
//   • weight, body-fat, BMI   → plain trend with a delta pill
//   • WHR, WHtR               → trend WITH a dashed threshold reference line
// Optional-first: a series with <2 points renders an honest empty state rather
// than a flat, meaningless line. Tabs are the design-system Tabs (sliding volt
// underline). The selected series also surfaces its start→latest delta.
// ============================================================================
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card, CardHeader, EmptyState, Tabs } from "../../components";
import { IconArrowDown } from "../../components/icons";
import type { Goal, MetricsSummary } from "../../api";
import { fmtNum, goalSeriesKey, normalizeSex, ratioThreshold } from "./lib";
import { TrendChart, type ChartAccent, type RefLine } from "./TrendChart";

type SeriesKey = "weight" | "body_fat" | "bmi" | "whr" | "whtr";

const META: Record<
  SeriesKey,
  { accent: ChartAccent; decimals: number; suffix: string; lowerIsBetter: boolean }
> = {
  weight: { accent: "coral", decimals: 1, suffix: " kg", lowerIsBetter: true },
  body_fat: { accent: "violet", decimals: 1, suffix: "%", lowerIsBetter: true },
  bmi: { accent: "ocean", decimals: 1, suffix: "", lowerIsBetter: true },
  whr: { accent: "amber", decimals: 2, suffix: "", lowerIsBetter: true },
  whtr: { accent: "lime", decimals: 2, suffix: "", lowerIsBetter: true },
};

interface TrendChartsProps {
  metrics: MetricsSummary;
  /** Active goals — used to draw a target reference line on the matching trend. */
  goals: Goal[];
}

export function TrendCharts({ metrics, goals }: TrendChartsProps) {
  const { t } = useTranslation("clientDetail");
  const sex = normalizeSex(metrics.sex);

  // Only offer tabs for series that actually have data.
  const available = useMemo(
    () =>
      (Object.keys(META) as SeriesKey[]).filter(
        (k) => (metrics.series[k]?.length ?? 0) >= 1,
      ),
    [metrics.series],
  );

  const [active, setActive] = useState<SeriesKey>(available[0] ?? "weight");
  const current = available.includes(active) ? active : available[0] ?? "weight";

  if (available.length === 0) {
    return (
      <Card>
        <CardHeader title={t("trends.title")} subtitle={t("trends.subtitle")} />
        <EmptyState
          title={t("trends.emptyTitle")}
          description={t("trends.emptyBlurb")}
          className="!py-12"
        />
      </Card>
    );
  }

  const series = metrics.series[current] ?? [];
  const meta = META[current];

  // Threshold reference line for ratio metrics.
  const refLines: RefLine[] = [];
  if (current === "whr" || current === "whtr") {
    refLines.push({
      value: ratioThreshold(current, sex),
      label: t("trends.thresholdLabel"),
      kind: "threshold",
    });
  }
  // Goal target reference line, if a matching active goal exists.
  const matchingGoal = goals.find(
    (g) =>
      g.status === "active" &&
      g.target_value != null &&
      goalSeriesKey(g.metric_key) === current,
  );
  if (matchingGoal?.target_value != null) {
    refLines.push({
      value: matchingGoal.target_value,
      label: t("trends.goalLabel"),
      kind: "goal",
    });
  }

  const first = series[0]?.y;
  const last = series[series.length - 1]?.y;
  const delta =
    first != null && last != null ? +(last - first).toFixed(2) : null;
  const improved =
    delta != null && (meta.lowerIsBetter ? delta <= 0 : delta >= 0);

  return (
    <Card>
      <CardHeader title={t("trends.title")} subtitle={t("trends.subtitle")} />

      <div className="mb-4 overflow-x-auto pb-1">
        <Tabs
          value={current}
          onChange={(id) => setActive(id as SeriesKey)}
          items={available.map((k) => ({
            id: k,
            label: t(`trends.tab.${k}`),
          }))}
        />
      </div>

      {series.length < 2 ? (
        <EmptyState
          title={t("trends.needMoreTitle")}
          description={t("trends.needMoreBlurb")}
          className="!py-10"
        />
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <span className="nums font-display text-3xl font-extrabold leading-none tracking-tightest text-body">
              {fmtNum(last as number, meta.decimals)}
              <span className="ml-1 text-base font-bold text-body-soft">
                {meta.suffix.trim()}
              </span>
            </span>
            {delta != null && delta !== 0 && (
              <Badge tone={improved ? "success" : "danger"} className="mb-1">
                <IconArrowDown
                  width={13}
                  height={13}
                  className={delta > 0 ? "rotate-180" : ""}
                />
                {delta > 0 ? "+" : ""}
                {fmtNum(delta, meta.decimals)}
                {meta.suffix}
              </Badge>
            )}
          </div>

          <TrendChart
            key={current}
            series={series}
            accent={meta.accent}
            decimals={meta.decimals}
            suffix={meta.suffix.trim()}
            refLines={refLines}
            ariaLabel={t("trends.chartAria", { metric: t(`trends.tab.${current}`) })}
            height={220}
          />
        </>
      )}
    </Card>
  );
}
